import {
    PARCEIRO_UFC,
    FUNDACAO_FASTEF,
    FUNDACAO_FCPC,
    UI_TEXTS,
} from '../config/prefill'; 
import { IDS } from '../partners';
import { salvarRascunho, carregarRascunho } from '../db';
import {
    getObjetivosGeraisHTML,
    getObjetivosEspecificosHTML,
    getJustificativaHTML,
    getResponsabilidadesUFCHTML,
    getResponsabilidadesParceiroHTML,
    getAtividadesConjuntasHTML,
    getDireitosPIHTML,
    getRecursosFinanceirosHTML,
    getInfraestruturaHTML
} from '../editor';
import { getIdentificacao } from '../partners';
import { getObjetoProjeto } from '../object';
import { getAtividades } from '../activities';
import type { DraftData } from '../types';


// —— Utilitário: encontra um campo por id ou name (mantendo sua convenção atual)
function findField(key: string): HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null {
    return (
        (document.getElementById(key) as any) ||
        (document.querySelector(`[name="${key}"]`) as any)
    );
}

// —— Define valor e dispara eventos para persistência/validação/“última edição”
function setFieldValue(key: string, value: string) {
    const el = findField(key);
    if (!el) {
        console.warn(`[prefill] Campo não encontrado para key/id: ${key}`);
        return;
    }
    const prior = (el as any).value;

    if ('value' in el) {
        (el as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement).value = value;
        el.dispatchEvent(new Event('input', { bubbles: true }));
        el.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
        (el as HTMLElement).textContent = value ?? '';
    }

    if (prior !== value) {
        document.dispatchEvent(new CustomEvent('form:autofilled', { detail: { key, value } }));
    }
}

// —— Limpar todos os campos de texto/área/select básicos
async function resetAllFields(): Promise<void> {
  // —— Lock de scroll sem flicker (body fixed)
  const sx = window.scrollX;
  const sy = window.scrollY;
  const html = document.documentElement as HTMLElement;
  const body = document.body as HTMLBodyElement;

  const prevScrollBehavior = html.style.scrollBehavior || '';
  const prevBodyPos = body.style.position || '';
  const prevBodyTop = body.style.top || '';
  const prevBodyWidth = body.style.width || '';
  const prevBodyOverflow = body.style.overflow || '';

  html.style.scrollBehavior = 'auto';       // evita animação
  body.style.position = 'fixed';            // trava a rolagem
  body.style.top = `-${sy}px`;              // mantém a posição atual
  body.style.width = '100%';
  body.style.overflow = 'hidden';
  (document.activeElement as HTMLElement | null)?.blur?.();

  try {
    // 1) Limpar inputs/textarea/select básicos
    const selectors = [
      'input[type="text"]',
      'input[type="search"]',
      'input[type="number"]',
      'input[type="tel"]',
      'input[type="email"]',
      'input[type="url"]',
      'textarea',
      'select',
    ].join(',');

    document
      .querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(selectors)
      .forEach((el) => {
        if ((el as HTMLInputElement).type === 'hidden') return;
        if (el.hasAttribute('data-skip-reset')) return;
        el.value = '';
        // Dispara eventos para quem persiste/valida
        el.dispatchEvent(new Event('input', { bubbles: true }));
        el.dispatchEvent(new Event('change', { bubbles: true }));
      });

    // 2) Limpar editores Quill 
    const quillContainers = document.querySelectorAll<HTMLElement>('.ql-container');
    quillContainers.forEach((container) => {
      const anyContainer = container as any;
      const quill =
        anyContainer.__quill ||
        (window as any).Quill?.find?.(container) ||
        null;

      if (quill && typeof quill.setContents === 'function') {
        quill.blur?.();
        quill.setContents([], 'silent');
        quill.setText('', 'silent');
        quill.history?.clear?.();
        // Não emitir 'text-change' aqui para não acionar handlers que podem focar e rolar
      } else {
        const editor = container.querySelector<HTMLElement>('.ql-editor');
        if (editor) {
          editor.innerHTML = '';
          editor.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }
    });

    // 2.4) Aguarda um frame para o DOM refletir o estado vazio
    await new Promise<void>((r) => requestAnimationFrame(() => r()));

    // 2.5) Persistir o estado (DraftData completo)
    try {
      const atuais = (await carregarRascunho()) || ({} as DraftData);
      const identificacao = getIdentificacao();
      const objeto = getObjetoProjeto();
      const atividades = getAtividades();

      const novo: DraftData = {
        ...atuais,
        ...identificacao,
        ...objeto,
        atividades,
        objetivosGerais: getObjetivosGeraisHTML(),
        objetivosEspecificos: getObjetivosEspecificosHTML(),
        justificativa: getJustificativaHTML(),
        responsabilidadesUFC: getResponsabilidadesUFCHTML(),
        responsabilidadesParceiro: getResponsabilidadesParceiroHTML(),
        atividadesConjuntas: getAtividadesConjuntasHTML(),
        direitosPI: getDireitosPIHTML(),
        recursosFinanceiros: getRecursosFinanceirosHTML(),
        infraestrutura: getInfraestruturaHTML(),
        tabelas_orcamento: atuais.tabelas_orcamento ?? [],
      };

      await salvarRascunho(novo);
    } catch (e) {
      console.warn('[prefill] Falha ao salvar rascunho após reset:', e);
    }

    // 3) Notificar quem escuta “reset” 
    document.dispatchEvent(new CustomEvent('form:reset'));
  } finally {
    body.style.position = prevBodyPos;
    body.style.top = prevBodyTop;
    body.style.width = prevBodyWidth;
    body.style.overflow = prevBodyOverflow;
    html.style.scrollBehavior = prevScrollBehavior;
    window.scrollTo(sx, sy);
  }
}

// —— Preenche 1º Parceiro — ICT Pública (usando IDS existentes)
function fillParceiro1UFC() {
    setFieldValue(IDS.razaoSocial, PARCEIRO_UFC.razaoSocial);
    setFieldValue(IDS.cnpj, PARCEIRO_UFC.cnpj);
    setFieldValue(IDS.endereco, PARCEIRO_UFC.endereco);
    setFieldValue(IDS.representanteLegal, PARCEIRO_UFC.representanteLegal);
    setFieldValue(IDS.cargo, PARCEIRO_UFC.cargoRepresentante);
    setFieldValue(IDS.telefone, PARCEIRO_UFC.telefone);
    setFieldValue(IDS.email, PARCEIRO_UFC.email);
}

// —— Preenche Fundação (FASTEF/FCPC) — usando nomenclatura existente
function fillFundacaoFASTEF() {
    setFieldValue(IDS.razaoSocialFundacao, FUNDACAO_FASTEF.razaoSocial);
    setFieldValue(IDS.cnpjFundacao, FUNDACAO_FASTEF.cnpj);
    setFieldValue(IDS.enderecoFundacao, FUNDACAO_FASTEF.endereco);
    setFieldValue(IDS.representanteLegalFundacao, FUNDACAO_FASTEF.representanteLegal);
    setFieldValue(IDS.cargoFundacao, FUNDACAO_FASTEF.cargoRepresentante);
    setFieldValue(IDS.telefoneFundacao, FUNDACAO_FASTEF.telefone);
    setFieldValue(IDS.emailFundacao, FUNDACAO_FASTEF.email);
}

function fillFundacaoFCPC() {
    setFieldValue(IDS.razaoSocialFundacao, FUNDACAO_FCPC.razaoSocial);
    setFieldValue(IDS.cnpjFundacao, FUNDACAO_FCPC.cnpj);
    setFieldValue(IDS.enderecoFundacao, FUNDACAO_FCPC.endereco);
    setFieldValue(IDS.representanteLegalFundacao, FUNDACAO_FCPC.representanteLegal);
    setFieldValue(IDS.cargoFundacao, FUNDACAO_FCPC.cargoRepresentante);
    setFieldValue(IDS.telefoneFundacao, FUNDACAO_FCPC.telefone);
    setFieldValue(IDS.emailFundacao, FUNDACAO_FCPC.email);
}

function formatTelefone(telefone: string): string {
  return telefone.replace(/\D/g, '').replace(/^(\d{2})(\d{4,5})(\d{4})$/, '($1) $2-$3');
}

function formatCEP(cep: string): string {
  return cep.replace(/\D/g, '').replace(/^(\d{5})(\d{3})$/, '$1-$2');
}

function formatCNPJ(value: string): string {
  const digits = value.replace(/\D/g, '');
  
  const limited = digits.substring(0, 14);

  return limited
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');
}

function setError(inputId: string, message: string | null) {
  const input = document.getElementById(inputId) as HTMLInputElement;
  if (!input) return;

  const existingError = input.parentElement?.querySelector('.error-message');
  if (existingError) existingError.remove();

  if (message) {
    input.classList.add('input-error');
    const errorSpan = document.createElement('span');
    errorSpan.className = 'error-message';
    errorSpan.innerText = message;
    input.parentElement?.appendChild(errorSpan);
  } else {
    input.classList.remove('input-error');
  }
}

async function fillInfoByCNPJ(cnpj: string, fields: Record<string, string>) {
  const cleanCNPJ = cnpj.replace(/\D/g, '');
  const cnpjInputId = fields.cnpjId; // Vamos precisar passar o ID do input de CNPJ nos fields

  // Limpa erro ao iniciar nova busca
  if (cnpjInputId) setError(cnpjInputId, null);

  if (cleanCNPJ.length !== 14) {
    if (cnpjInputId) setError(cnpjInputId, "CNPJ deve ter 14 dígitos.");
    return;
  }

  try {
    const response = await fetch(`https://api.opencnpj.org/${cleanCNPJ}?dataset=receita`);
    
    if (!response.ok) {
        throw new Error('Empresa não encontrada');
    }
    
    const data = await response.json();

    const map = {
      razaoSocial: data.razao_social,
      endereco: `${data.tipo_logradouro} ${data.logradouro}, n° ${data.numero} - ${data.bairro}, ${data.municipio}/${data.uf} - ${(data.cep)}`,
      email: data.email?.toLowerCase() || '',
      telefone: data.ddd_telefone_1 || '', 
    };

    Object.entries(fields).forEach(([key, id]) => {
      const input = document.getElementById(id) as HTMLInputElement;
      if (input && map[key as keyof typeof map]) {
        input.value = map[key as keyof typeof map];
      }
    });

  } catch (error) {
    console.error("Erro ao buscar CNPJ:", error);
    // Em vez do alert, usamos o feedback visual no input
    if (cnpjInputId) {
        setError(cnpjInputId, "CNPJ inválido ou não encontrado.");
    }
  }
}
// —— Liga os botões aos handlers
export function initPrefillActions(root: Document | HTMLElement = document) {
    const btnReset = root.querySelector<HTMLButtonElement>('#btnReset');
    const btnUFC = root.querySelector<HTMLButtonElement>('#btnAutofillUFC');
    const btnFASTEF = root.querySelector<HTMLButtonElement>('#btnAutofillFASTEF');
    const btnFCPC = root.querySelector<HTMLButtonElement>('#btnAutofillFCPC');

    if (btnReset) btnReset.textContent = UI_TEXTS.resetButton;
    if (btnUFC) btnUFC.textContent = UI_TEXTS.autofillUFCButton;
    if (btnFASTEF) btnFASTEF.textContent = UI_TEXTS.autofillFASTEFButton;
    if (btnFCPC) btnFCPC.textContent = UI_TEXTS.autofillFCPCButton;

    const cnpjConfigs = [
        { 
            inputID: 'cnpj', 
            fields: { cnpjId: 'cnpj', razaoSocial: 'razaoSocial', endereco: 'endereco', email: 'email', telefone: 'telefone' } 
        },
        { 
            inputID: 'cnpj2', 
            fields: { cnpjId: 'cnpj2', razaoSocial: 'razaoSocial2', endereco: 'endereco2', email: 'email2', telefone: 'telefone2' } 
        },
        { 
            inputID: 'cnpjFundacao', 
            fields: { cnpjId: 'cnpjFundacao', razaoSocial: 'razaoSocialFundacao', endereco: 'enderecoFundacao', email: 'emailFundacao', telefone: 'telefoneFundacao' } 
        }
    ];

    cnpjConfigs.forEach(config => {
        const cnpjInput = root.querySelector<HTMLInputElement>(`#${config.inputID}`);
        
        if (cnpjInput) {
            // Evento enquanto digita: Formata e LIMPA o erro visual
            cnpjInput.addEventListener('input', (e) => {
                const target = e.target as HTMLInputElement;
                target.value = formatCNPJ(target.value);
                
                // Se o usuário está corrigindo, removemos a borda vermelha e a mensagem
                setError(config.inputID, null); 
            });

            // Evento ao sair do campo: Valida e busca na API
            cnpjInput.addEventListener('blur', async () => {
                const val = cnpjInput.value.replace(/\D/g, '');
                
                if (val.length === 0) {
                    setError(config.inputID, null); // Campo vazio não deve mostrar erro
                    return;
                }

                if (val.length !== 14) {
                    setError(config.inputID, "CNPJ inválido. Verifique os números.");
                    return;
                }

                // Se passou na validação local, tenta a API
                await fillInfoByCNPJ(val, config.fields);
            });
        }
    });
    
    btnReset?.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        cnpjConfigs.forEach(c => setError(c.inputID, null));
        resetAllFields();
    });

    btnUFC?.addEventListener('click', () => fillParceiro1UFC());
    btnFASTEF?.addEventListener('click', () => fillFundacaoFASTEF());
    btnFCPC?.addEventListener('click', () => fillFundacaoFCPC());
}